import type { BusinessSummary, VendorProfile } from '@cravo/core'

const DASHBOARD_STATUS_PRIORITY: BusinessSummary['status'][] = [
  'APPROVED',
  'PENDING_APPROVAL',
  'NEEDS_RESUBMISSION',
  'REJECTED',
  'SUSPENDED',
]

const ONBOARDING_STATUS_PRIORITY: BusinessSummary['status'][] = [
  'PENDING_APPROVAL',
  'NEEDS_RESUBMISSION',
  'REJECTED',
  'SUSPENDED',
  'APPROVED',
]

/**
 * Pick the highest-priority business by status.
 * - When `strict` is true, returns null if no business matches any of the
 *   given priorities (no first-business fallback). Use this for status-strict
 *   checks like "is there an APPROVED business".
 * - When `strict` is false, falls back to `businesses[0]` if nothing matched
 *   — useful for "show *some* business" UI cases.
 */
function pickBusinessByPriority(
  businesses: BusinessSummary[] | undefined,
  priorities: readonly BusinessSummary['status'][],
  strict = false,
): BusinessSummary | null {
  if (!businesses?.length) return null

  for (const status of priorities) {
    const match = businesses.find((business) => business.status === status)
    if (match) return match
  }

  if (strict) return null
  return businesses[0] ?? null
}

/**
 * Returns the APPROVED business or `null`. STRICT — no fallback. This is the
 * gate used by BusinessGuard to decide "can the user enter the dashboard?".
 * Bug fix: previously fell back to `businesses[0]`, which let a PENDING vendor
 * land on the dashboard after re-login.
 */
export function getApprovedBusiness(vendor: VendorProfile | null | undefined): BusinessSummary | null {
  return pickBusinessByPriority(vendor?.businesses, ['APPROVED'], /* strict */ true)
}

export function getDashboardBusiness(vendor: VendorProfile | null | undefined): BusinessSummary | null {
  return pickBusinessByPriority(vendor?.businesses, DASHBOARD_STATUS_PRIORITY)
}

export function getOnboardingBusiness(vendor: VendorProfile | null | undefined): BusinessSummary | null {
  return pickBusinessByPriority(vendor?.businesses, ONBOARDING_STATUS_PRIORITY)
}

/**
 * Resolve a business object's entity type defensively to either
 * 'RESTAURANT' or 'STORE'. The TS type advertises `entityType` (upper),
 * but the backend's `mapBusinessSummary` returns `type` (lower) and
 * `getBusinessDetail` returns `type` too — so any caller that reads
 * `business.entityType` directly silently gets `undefined` for store
 * vendors and falls back to restaurant branches.
 *
 * Treats RESTAURANT as the default when the field is missing or
 * unrecognised — safer fallback for legacy data and unauthenticated
 * states where the response shape is unknown.
 */
export function resolveBusinessEntityType(
  business: BusinessSummary | null | undefined,
): 'RESTAURANT' | 'STORE' {
  if (!business) return 'RESTAURANT'
  const raw =
    (business as unknown as { entityType?: string; type?: string }).entityType ??
    (business as unknown as { type?: string }).type ??
    ''
  return String(raw).toUpperCase() === 'STORE' ? 'STORE' : 'RESTAURANT'
}

/**
 * Resolve a business object's display name defensively. The TS type
 * advertises `businessName`, but auth/detail responses send `name` —
 * so direct `business.businessName` reads land empty and printer
 * tickets / settings panels fall through to the owner's full name
 * (which looks confusing on a customer-facing receipt). Returns `null`
 * when no name can be resolved; callers should then use their own
 * fallback (typically `vendor.fullName`).
 */
export function resolveBusinessName(
  business: BusinessSummary | null | undefined,
): string | null {
  if (!business) return null
  const anyShape = business as unknown as { businessName?: string; name?: string }
  return anyShape.businessName ?? anyShape.name ?? null
}
